import React, { createContext, useContext, useMemo } from "react";
import { Tab, TabList, Tooltip } from "@mui/joy";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { KeyboardCoordinateGetter } from "@dnd-kit/core";

export interface CanvasPanel {
  id: string;
  label: string;
  icon?: string;
}
interface TabMoveContext {
  move: (ownerId: string, panelId: string, index: number) => Promise<void>;
  disabled: boolean;
  reason?: string;
}
export const CanvasTabMoveContext = createContext<TabMoveContext | null>(null);

function DraggableTab({
  panel,
  disabled,
  reason,
}: {
  panel: CanvasPanel;
  disabled: boolean;
  reason?: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({ id: panel.id, disabled });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: panel.id,
    disabled,
  });
  return (
    <Tab
      component="div"
      value={panel.id}
      ref={setDropRef}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        outline: isOver ? "2px solid" : undefined,
        outlineColor: "primary.500",
      }}
    >
      <Tooltip
        title={
          reason || "Drag to reorder; Space picks up, arrows move, Space drops."
        }
      >
        <span
          ref={setDragRef}
          {...attributes}
          {...listeners}
          role="button"
          aria-label={`Reorder ${panel.label}`}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onPointerDown={(event) => {
            listeners?.onPointerDown?.(event);
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            listeners?.onKeyDown?.(event);
            event.stopPropagation();
          }}
          onClick={(event) => event.stopPropagation()}
          style={{
            display: "inline-flex",
            cursor: disabled ? "default" : "grab",
            touchAction: "none",
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </span>
      </Tooltip>
      {panel.label}
    </Tab>
  );
}

/** A separate DnD scope prevents tab drags from moving their container on the grid. */
export function CanvasTabHeaders({
  ownerId,
  panels,
}: {
  ownerId: string;
  panels: CanvasPanel[];
}) {
  const moveContext = useContext(CanvasTabMoveContext);
  const coordinateGetter = useMemo<KeyboardCoordinateGetter>(
    () =>
      (event, { context, currentCoordinates }) => {
        const direction =
          event.code === "ArrowRight" ? 1 : event.code === "ArrowLeft" ? -1 : 0;
        if (!direction) return undefined;
        event.preventDefault();
        const currentId = context.over?.id ?? context.active?.id;
        const index = panels.findIndex((panel) => panel.id === currentId);
        const next = panels[index + direction];
        const rect = next && context.droppableRects.get(next.id);
        if (!rect) return currentCoordinates;
        const activeRect = context.collisionRect;
        return activeRect
          ? {
              x:
                currentCoordinates.x +
                rect.left +
                rect.width / 2 -
                activeRect.left -
                activeRect.width / 2,
              y:
                currentCoordinates.y +
                rect.top +
                rect.height / 2 -
                activeRect.top -
                activeRect.height / 2,
            }
          : currentCoordinates;
      },
    [panels],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter }),
  );
  if (!moveContext)
    return (
      <TabList>
        {panels.map((panel) => (
          <Tab key={panel.id} value={panel.id}>
            {panel.label}
          </Tab>
        ))}
      </TabList>
    );
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      accessibility={{
        screenReaderInstructions: {
          draggable:
            "Press Space to pick up a tab, Left or Right to move it, Space to drop, or Escape to cancel.",
        },
      }}
      onDragEnd={({ active, over }) => {
        if (!over || active.id === over.id || moveContext.disabled) return;
        const index = panels.findIndex((panel) => panel.id === over.id);
        if (index >= 0)
          void moveContext.move(ownerId, String(active.id), index);
      }}
    >
      <TabList>
        {panels.map((panel) => (
          <DraggableTab
            key={panel.id}
            panel={panel}
            disabled={moveContext.disabled}
            reason={moveContext.reason}
          />
        ))}
      </TabList>
    </DndContext>
  );
}
